if (!window.__MARGALO_FREE_GIFT_LOADED__) {
  window.__MARGALO_FREE_GIFT_LOADED__ = true;

  (() => {
    const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const ELIGIBLE_PRODUCT_ID = 16223611519325; // Already Late
    const GIFT_VARIANT_ID = 65741364494685; // Watch Link Remover
    const GIFT_PROPERTY = '_margalo_free_watch_tool';
    const SOURCE = 'margalo-free-gift';

    let syncing = false;
    let queued = false;
    let timer = null;

    function cartSectionIds() {
      return [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((el) => el.dataset.sectionId)
        .filter(Boolean)
        .filter((id, index, all) => all.indexOf(id) === index);
    }

    async function getCart() {
      const response = await fetch(`${ROOT}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
      return response.json();
    }

    function isEligibleLine(item) {
      return Number(item.product_id) === ELIGIBLE_PRODUCT_ID && Number(item.quantity) > 0;
    }

    function isGiftVariant(item) {
      return Number(item.variant_id) === GIFT_VARIANT_ID && Number(item.quantity) > 0;
    }

    function isAutomaticGiftLine(item) {
      return (
        isGiftVariant(item) &&
        item.properties &&
        String(item.properties[GIFT_PROPERTY]) === '1'
      );
    }

    function isPaidOrManualGiftLine(item) {
      return isGiftVariant(item) && !isAutomaticGiftLine(item);
    }

    function isActuallyFree(item) {
      return Number(item.final_line_price) === 0 || Number(item.final_price) === 0;
    }

    async function cartMutation(endpoint, payload) {
      const sectionIds = cartSectionIds();
      if (sectionIds.length) payload.sections = sectionIds.join(',');

      const response = await fetch(`${ROOT}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || `Cart mutation failed (${response.status})`);
      }
      return data;
    }

    function announceCartUpdate(cart, sections = {}) {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: SOURCE,
            data: {
              source: SOURCE,
              itemCount: cart.item_count,
              sections,
            },
          },
        })
      );

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { resource: cart, source: SOURCE },
        })
      );
    }

    async function removeLines(lines) {
      let sections = {};
      for (const line of lines) {
        const result = await cartMutation('cart/change.js', { id: line.key, quantity: 0 });
        sections = result.sections || sections;
      }
      return sections;
    }

    async function syncFreeGift(cartHint) {
      if (syncing) {
        queued = true;
        return;
      }

      syncing = true;
      try {
        let cart = cartHint && Array.isArray(cartHint.items) ? cartHint : await getCart();
        let changed = false;
        let sections = {};
        const hasAlreadyLate = cart.items.some(isEligibleLine);

        // Without an eligible watch, only remove the automatically injected gift.
        // A manually purchased Watch Link Remover is left untouched.
        if (!hasAlreadyLate) {
          const automaticGiftLines = cart.items.filter(isAutomaticGiftLine);
          if (automaticGiftLines.length) {
            sections = await removeLines(automaticGiftLines);
            cart = await getCart();
            announceCartUpdate(cart, sections);
          }
          return;
        }

        // With an eligible watch, a separately added paid remover must not coexist with the free gift.
        const manualGiftLines = cart.items.filter(isPaidOrManualGiftLine);
        if (manualGiftLines.length) {
          sections = await removeLines(manualGiftLines);
          cart = await getCart();
          changed = true;
        }

        let automaticGiftLines = cart.items.filter(isAutomaticGiftLine);

        // Add the gift once if it is missing.
        if (automaticGiftLines.length === 0) {
          const result = await cartMutation('cart/add.js', {
            items: [
              {
                id: GIFT_VARIANT_ID,
                quantity: 1,
                properties: { [GIFT_PROPERTY]: '1' },
              },
            ],
          });
          sections = result.sections || sections;
          cart = await getCart();
          automaticGiftLines = cart.items.filter(isAutomaticGiftLine);
          changed = true;
        }

        // Exactly one gift per order, quantity one.
        if (automaticGiftLines.length) {
          const [firstGift, ...duplicates] = automaticGiftLines;

          if (Number(firstGift.quantity) !== 1) {
            const result = await cartMutation('cart/change.js', { id: firstGift.key, quantity: 1 });
            sections = result.sections || sections;
            cart = await getCart();
            changed = true;
          }

          if (duplicates.length) {
            sections = await removeLines(duplicates);
            cart = await getCart();
            changed = true;
          }
        }

        // Safety net: never leave an automatically injected “free gift” in the cart at a paid price.
        // Shopify's FREE WATCH TOOL automatic discount should reduce this line to 0.
        const currentAutoGift = cart.items.find(isAutomaticGiftLine);
        if (currentAutoGift && !isActuallyFree(currentAutoGift)) {
          sections = await removeLines([currentAutoGift]);
          cart = await getCart();
          changed = true;
          console.error('[Margalo] Free Watch Tool discount did not reduce the gift to 0. Gift removed to prevent a paid charge.');
        }

        if (changed) announceCartUpdate(cart, sections);
      } catch (error) {
        console.error('[Margalo] Free Watch Tool:', error);
      } finally {
        syncing = false;
        if (queued) {
          queued = false;
          scheduleSync();
        }
      }
    }

    function scheduleSync(cartHint) {
      clearTimeout(timer);
      timer = setTimeout(() => syncFreeGift(cartHint), 100);
    }

    document.addEventListener('cart:update', (event) => {
      if (event.detail?.data?.source === SOURCE) return;
      scheduleSync(event.detail?.resource);
    });

    document.addEventListener('cart:updated', (event) => {
      if (event.detail?.source === SOURCE) return;
      scheduleSync(event.detail?.resource);
    });

    document.addEventListener('cart:refresh', () => scheduleSync());
    document.addEventListener('shopify:section:load', () => scheduleSync());

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => scheduleSync(), { once: true });
    } else {
      scheduleSync();
    }

    window.MargaloFreeGift = { sync: () => scheduleSync() };
  })();
}
